import type { ContactEmailItem } from '../../services/ContactEmailService';

/** Canal da lista de Conversas. */
export type ConversationChannelFilter = 'all' | 'whatsapp' | 'email' | 'instagram' | 'messenger' | 'webchat';

/** Por onde se responde nesta conversa. */
export type ReplyChannel = 'whatsapp' | 'email' | 'instagram' | 'messenger' | 'webchat';

export const CHANNEL_FILTER_OPTIONS: { value: ConversationChannelFilter; label: string }[] = [
  { value: 'all', label: 'Todos os canais' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
];

/** Opções do filtro "Canal": Instagram e Messenger só entram com conta ativa no setor, e o Site só com site ativo. */
export function channelFilterOptions(available: { instagram?: boolean; messenger?: boolean; webchat?: boolean }): { value: ConversationChannelFilter; label: string }[] {
  const options = [...CHANNEL_FILTER_OPTIONS];
  if (available.instagram) options.push({ value: 'instagram', label: 'Instagram' });
  if (available.messenger) options.push({ value: 'messenger', label: 'Messenger' });
  if (available.webchat) options.push({ value: 'webchat', label: 'Site' });
  return options;
}

export type TimelineItem<M> =
  | { kind: 'whatsapp'; key: string; at: number; message: M }
  | { kind: 'email'; key: string; at: number; email: ContactEmailItem };

function time(value: string | null | undefined): number {
  if (!value) return 0;
  const t = Date.parse(value);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Linha do tempo única do cliente: mensagens de WhatsApp e e-mails na ordem do tempo. Empate mantém o WhatsApp
 * antes (ordem estável), e as mensagens de WhatsApp preservam a ordem em que chegaram.
 */
export function mergeConversationTimeline<M extends { id: number | string; sentAt: string }>(
  messages: M[],
  emails: ContactEmailItem[],
): TimelineItem<M>[] {
  const items: TimelineItem<M>[] = [
    ...messages.map((message, index) => ({ kind: 'whatsapp' as const, key: `wa-${message.id}-${index}`, at: time(message.sentAt), message })),
    ...emails.map((email) => ({ kind: 'email' as const, key: `email-${email.id}`, at: time(email.at), email })),
  ];
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (a.item.at - b.item.at) || (a.item.kind === b.item.kind ? a.index - b.index : a.item.kind === 'whatsapp' ? -1 : 1))
    .map(({ item }) => item);
}

/** Linha da lista combina com o filtro de canal (o servidor já filtra; isto evita piscar com dados locais). */
export function matchesChannelFilter(
  contact: { number?: string | null; hasEmail?: boolean; hasWhatsApp?: boolean; hasInstagram?: boolean; hasMessenger?: boolean; hasWebchat?: boolean },
  filter: ConversationChannelFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'email') return contact.hasEmail === true;
  if (filter === 'instagram') return contact.hasInstagram === true;
  if (filter === 'messenger') return contact.hasMessenger === true;
  if (filter === 'webchat') return contact.hasWebchat === true;
  const hasWhatsApp = contact.hasWhatsApp ?? Boolean((contact.number || '').trim());
  return hasWhatsApp;
}

/** Contato só de e-mail (sem WhatsApp, Instagram, Messenger nem Site): a conversa abre direto na resposta por e-mail. */
export function isEmailOnlyContact(
  contact: { number?: string | null; hasWhatsApp?: boolean; hasInstagram?: boolean; hasMessenger?: boolean; hasWebchat?: boolean } | null | undefined,
): boolean {
  if (!contact) return false;
  if (contact.hasInstagram === true || contact.hasMessenger === true || contact.hasWebchat === true) return false;
  if (typeof contact.hasWhatsApp === 'boolean') return !contact.hasWhatsApp;
  return !(contact.number || '').trim();
}

/**
 * Cliente que só escreve pelo Instagram (sem WhatsApp) e o setor não tem conta do Instagram ativa: não há por onde responder.
 * A tela mostra "Instagram precisa reconectar" no lugar do compositor do WhatsApp.
 */
export function needsInstagramReconnect(
  contact: { number?: string | null; hasWhatsApp?: boolean; hasInstagram?: boolean } | null | undefined,
  available: { instagram?: boolean },
): boolean {
  if (!contact || contact.hasInstagram !== true || available.instagram) return false;
  const hasWhatsApp = typeof contact.hasWhatsApp === 'boolean' ? contact.hasWhatsApp : Boolean((contact.number || '').trim());
  return !hasWhatsApp;
}

/**
 * Canal em que a conversa abre: quem acabou de falar pelo Site (e tem a identidade do Site) no Site, mesmo que já tenha WhatsApp;
 * cliente só do Instagram ou do Messenger (com a conta ativa) nesse canal (o da última mensagem, se tiver os dois); só de e-mail
 * no e-mail; o resto no WhatsApp.
 */
export function defaultReplyChannel(
  contact: { number?: string | null; hasWhatsApp?: boolean; hasInstagram?: boolean; hasMessenger?: boolean; hasWebchat?: boolean; lastMessageChannel?: string | null } | null | undefined,
  available: { instagram?: boolean; messenger?: boolean },
): ReplyChannel {
  if (!contact) return 'whatsapp';
  // O visitante do site que passou o telefone continua esperando a resposta no chat do site.
  if (contact.lastMessageChannel === 'webchat' && contact.hasWebchat === true) return 'webchat';
  const hasWhatsApp = typeof contact.hasWhatsApp === 'boolean' ? contact.hasWhatsApp : Boolean((contact.number || '').trim());
  const instagram = contact.hasInstagram === true && available.instagram === true;
  const messenger = contact.hasMessenger === true && available.messenger === true;
  if (!hasWhatsApp && instagram && messenger) return contact.lastMessageChannel === 'messenger' ? 'messenger' : 'instagram';
  if (!hasWhatsApp && instagram) return 'instagram';
  if (!hasWhatsApp && messenger) return 'messenger';
  // O Site responde mesmo com o site pausado: a identidade do visitante basta.
  if (!hasWhatsApp && contact.hasWebchat === true) return 'webchat';
  return isEmailOnlyContact(contact) ? 'email' : 'whatsapp';
}

/** E-mail mais recente do cliente (o que a resposta vai citar). */
export function latestEmail(emails: ContactEmailItem[]): ContactEmailItem | null {
  return emails.reduce<ContactEmailItem | null>((best, e) => (!best || time(e.at) >= time(best.at) ? e : best), null);
}
